/**
 * Unified Quality Configuration
 * 
 * Re-exports quality rules from Single Source of Truth (src/constants/qualityRules.ts)
 * and adds domain-specific configuration for SEO, scripts, and entity thresholds.
 * 
 * RULE COUNT: 31 total (17 Error + 11 Warning + 3 Info)
 * 
 * @see src/constants/qualityRules.ts - Single source of truth for issue taxonomy
 * @sync BACKEND: supabase/functions/_shared/config/quality-config.ts
 * @sync BACKEND: supabase/functions/_shared/config/field-compliance-config.ts
 * @test src/config/__tests__/qualityConfigParity.test.ts
 */

import type { LocalizationContentType } from '@/constants/localization';
import { 
  ENTITY_QUALITY_THRESHOLDS, 
  type QualityThresholds,
} from './entityQualityThresholds';
import { 
  getCompletenessFieldsForEntity, 
  entityRequiresSEO,
  ENTITY_EXCLUDED_FIELDS,
} from './metadataGovernance';

// ============================================================================
// RE-EXPORTS FROM SINGLE SOURCE OF TRUTH
// ============================================================================

export {
  // Types
  type QualityIssueType,
  type QualityIssueSeverity,
  type QualityIssueCategory,
  type QualityFixAction,
  type QualityRule,
  // Constants
  QUALITY_RULES,
  QUALITY_RULES_BY_TYPE,
  ALL_ISSUE_TYPES,
  QUALITY_RULE_COUNTS,
  // Helper functions
  getIssueSeverity,
  getIssueLabel,
  getQualityRule,
  isFixable,
  getIssuesBySeverity,
  getIssuesByCategory,
} from '@/constants/qualityRules';

// Re-import for local use
import {
  QUALITY_RULES_BY_TYPE,
  type QualityIssueType,
  type QualityIssueSeverity,
} from '@/constants/qualityRules';

// ============================================================================
// RE-EXPORTS FROM OTHER CONFIGS
// ============================================================================

export { ENTITY_QUALITY_THRESHOLDS, type QualityThresholds };
export { getCompletenessFieldsForEntity, entityRequiresSEO, ENTITY_EXCLUDED_FIELDS };

// ============================================================================
// BACKWARD COMPATIBILITY - ISSUE_TAXONOMY
// ============================================================================

/**
 * @deprecated Use QUALITY_RULES_BY_TYPE from @/constants/qualityRules instead
 * Maintained for backward compatibility with existing imports
 */
export interface IssueTaxonomyEntry {
  severity: QualityIssueSeverity;
  label: string;
  description: string;
  fixable: boolean;
  fixAction: 'regenerate' | 'translate' | 'seed' | 'manual';
  category: 'single-record' | 'cross-record' | 'backend';
}

/**
 * @deprecated Use QUALITY_RULES_BY_TYPE from @/constants/qualityRules instead
 * Complete issue type taxonomy with metadata - backward compatible format
 */
export const ISSUE_TAXONOMY: Record<QualityIssueType, IssueTaxonomyEntry> = 
  Object.fromEntries(
    Object.entries(QUALITY_RULES_BY_TYPE).map(([type, rule]) => [
      type,
      {
        severity: rule.severity,
        label: rule.label,
        description: rule.description,
        fixable: rule.fixable,
        fixAction: rule.fixAction === 'auto' || rule.fixAction === 'delete' || rule.fixAction === 'none' 
          ? 'manual' 
          : rule.fixAction,
        category: rule.category,
      }
    ])
  ) as Record<QualityIssueType, IssueTaxonomyEntry>;

// ============================================================================
// SEO FIELD CONSTRAINTS
// ============================================================================

/**
 * SEO Field Constraints - Google 2025 Best Practices
 * 
 * RATIONALE:
 * - meta_title max 60: Google uses ~600px width (≈60 chars for Latin scripts)
 * - meta_description min 120: Modern SERPs display longer snippets; <120 looks incomplete
 * - aio_snippet min 150: AI Overviews need more context for quality responses
 * 
 * @see docs/METADATA_GOVERNANCE.md for enforcement details
 */
export const SEO_FIELD_CONSTRAINTS = {
  meta_title: { min: 30, max: 60 },       // KEEP - 600px ≈ 60 chars for Latin
  meta_description: { min: 120, max: 160 }, // RAISED min from 100→120 (CTR optimization)
  og_description: { min: 120, max: 200 },   // RAISED min to match meta_description
  aio_snippet: { min: 150, max: 300 },      // RAISED min from 80→150 (AI Overview optimization)
} as const;

export type SeoConstraintField = keyof typeof SEO_FIELD_CONSTRAINTS;

// ============================================================================
// SCRIPT CONFIGURATION
// ============================================================================

/**
 * Script detection regex patterns
 */
export const SCRIPT_PATTERNS: Record<string, RegExp> = {
  cjk: /[\u4E00-\u9FFF\u3040-\u309F\u30A0-\u30FF\uAC00-\uD7AF]/g,
  rtl: /[\u0600-\u06FF\u0590-\u05FF]/g,
  cyrillic: /[\u0400-\u04FF]/g,
  latin: /[A-Za-z\u00C0-\u024F]/g,
  devanagari: /[\u0900-\u097F]/g,
  thai: /[\u0E00-\u0E7F]/g,
  // COMPLETE Indic pattern - all 10 South Asian scripts
  // Devanagari, Bengali, Gurmukhi, Gujarati, Oriya, Tamil, Telugu, Kannada, Malayalam, Thai
  indic: /[\u0900-\u097F\u0980-\u09FF\u0A00-\u0A7F\u0A80-\u0AFF\u0B00-\u0B7F\u0B80-\u0BFF\u0C00-\u0C7F\u0C80-\u0CFF\u0D00-\u0D7F\u0E00-\u0E7F]/g,
  greek: /[\u0370-\u03FF]/g,
};

/**
 * Length multipliers for script-aware validation
 * Logographic scripts (CJK) have higher density - fewer chars needed
 */
export const SCRIPT_LENGTH_MULTIPLIERS: Record<string, number> = {
  cjk: 0.5,       // Chinese, Japanese, Korean
  rtl: 0.5,       // Arabic, Hebrew, Persian
  indic: 0.5,     // Hindi, Thai, Tamil, Marathi, Telugu, Punjabi, Bengali
  cyrillic: 0.8,  // Russian, Ukrainian, Bulgarian, Serbian
  greek: 0.9,     // Greek (slightly denser than Latin)
  latin: 1.0,     // Default
} as const;

/**
 * Map locale base codes to script types
 */
export const LOCALE_SCRIPT_MAP: Record<string, string> = {
  // CJK
  zh: 'cjk', ja: 'cjk', ko: 'cjk',
  // RTL
  ar: 'rtl', he: 'rtl', fa: 'rtl',
  // Indic (complete list)
  hi: 'indic', th: 'indic', ta: 'indic', bn: 'indic',
  mr: 'indic', te: 'indic', pa: 'indic',
  // Greek
  el: 'greek',
  // Cyrillic
  ru: 'cyrillic', uk: 'cyrillic', bg: 'cyrillic', sr: 'cyrillic',
  // Latin (default)
  en: 'latin', de: 'latin', fr: 'latin', es: 'latin', it: 'latin', 
  pt: 'latin', nl: 'latin', pl: 'latin', sv: 'latin', da: 'latin',
  tr: 'latin', ro: 'latin', cs: 'latin', hu: 'latin', fi: 'latin',
  nb: 'latin', ms: 'latin', id: 'latin', vi: 'latin', tl: 'latin',
} as const;

/**
 * Get script type for a locale
 */
export function getScriptTypeForLocale(
  locale: string,
  localeConfigs?: Array<{ locale: string; script_type?: string | null }>
): string {
  // Check custom configs first
  if (localeConfigs) {
    const config = localeConfigs.find(c => c.locale === locale);
    if (config?.script_type) return config.script_type;
  }
  
  // Fallback to base language mapping
  const base = locale.split('-')[0];
  return LOCALE_SCRIPT_MAP[base] || 'latin';
}

// ============================================================================
// UNIFIED FIELD CONSTRAINT RESOLVER
// ============================================================================

/**
 * Get field constraints for entity + field + script combination
 * Merges entity-specific thresholds with SEO constraints
 * 
 * IMPORTANT: Distinguishes between:
 * - translated_primary → uses minNameLength (entity names can be short)
 * - meta_title → uses minTitleLength (SEO titles need substance per Google 2025)
 * 
 * @param entityType - Entity type (location, publication, etc.)
 * @param field - Field name (meta_title, translated_body, etc.)
 * @param scriptType - Script type (cjk, rtl, latin, etc.)
 * @returns { min, max } constraints in characters
 */
export function getFieldConstraints(
  entityType: LocalizationContentType,
  field: string,
  scriptType: string = 'latin'
): { min: number; max: number } {
  const thresholds = ENTITY_QUALITY_THRESHOLDS[entityType];
  if (!thresholds) {
    return { min: 0, max: Infinity };
  }
  
  const multiplier = SCRIPT_LENGTH_MULTIPLIERS[scriptType] || 1.0;
  
  // Entity name (translated_name) - uses minNameLength (short names OK)
  // NOTE: translated_primary renamed to translated_name (EN SOT migration)
  if (field === 'translated_name') {
    return {
      min: Math.floor(thresholds.minNameLength * multiplier),
      max: Math.floor(thresholds.maxNameLength / (multiplier < 1 ? multiplier : 1)),
    };
  }
  
  // SEO title (meta_title) - uses minTitleLength (Google 2025 standards)
  if (field === 'meta_title') {
    return {
      min: Math.floor(thresholds.minTitleLength * multiplier),
      max: Math.floor(thresholds.maxTitleLength / (multiplier < 1 ? multiplier : 1)),
    };
  }
  
  // NOTE: translated_primary_short REMOVED from codebase
  // Route taglines now use translated_body_short
  // Location short names remain in entity_fields.short_name (source table)
  
  // Excerpt fields (translated_body_short) - use excerpt-specific thresholds
  // Excerpts are distinct from full body: 50-700 chars vs 200+ chars
  // MUST match backend getEntityFieldConstraints() in field-compliance-config.ts
  if (field === 'translated_body_short') {
    return {
      min: Math.floor((thresholds.minExcerptLength ?? 30) * multiplier),
      max: Math.floor((thresholds.maxExcerptLength ?? 700) / (multiplier < 1 ? multiplier : 1)),
    };
  }
  
  // Full body fields - use minBodyLength (large content)
  if (field === 'translated_body') {
    return {
      min: Math.floor(thresholds.minBodyLength * multiplier),
      max: Infinity,
    };
  }
  
  // Description fields: merge SEO constraints with entity maxDescLength
  if (['meta_description', 'og_description'].includes(field)) {
    const seoMin = SEO_FIELD_CONSTRAINTS.meta_description?.min || 100;
    return {
      min: Math.floor(seoMin * multiplier),
      max: Math.floor(thresholds.maxDescLength / (multiplier < 1 ? multiplier : 1)),
    };
  }
  
  // AIO fields
  if (field === 'aio_snippet') {
    const constraint = SEO_FIELD_CONSTRAINTS.aio_snippet;
    return {
      min: Math.floor(constraint.min * multiplier),
      max: Math.floor(constraint.max / (multiplier < 1 ? multiplier : 1)),
    };
  }
  
  // Default: no constraints
  return { min: 0, max: Infinity };
}

/**
 * Check if entity requires SEO validation (has standalone pages)
 */
export function entityRequiresHreflang(entityType: LocalizationContentType): boolean {
  // Only entities with standalone pages need hreflang
  return entityRequiresSEO(entityType);
}

// ============================================================================
// VALIDATION PATTERN CONSTANTS
// ============================================================================

export const PLACEHOLDER_PATTERNS = [
  /\[TRANSLATE\]/i,
  /\[TODO\]/i,
  /\[TBD\]/i,
  /\[PENDING\]/i,
  /\[MISSING\]/i,
  /Lorem ipsum/i,
  /Dolor sit amet/i,
  /^\s*TODO\s*$/i,
  /^\s*TBD\s*$/i,
  /^\s*N\/A\s*$/i,
  /^\s*-\s*$/,
  /^\s*\.\.\.\s*$/,
];

/**
 * STRICT encoding error patterns - detect real mojibake/corruption only.
 * Used for HTML body fields where entities like &nbsp; are legitimate.
 */
export const ENCODING_ERROR_PATTERNS_STRICT = [
  /Ã[\x80-\xBF]/,            // UTF-8 mojibake (e.g., Ã©)
  /â€[™"]/,                   // Smart quotes mojibake
  /\\u[0-9A-Fa-f]{4}/,        // Escaped unicode (e.g., \u0E01)
];

/**
 * FULL encoding error patterns - includes HTML entities.
 * Used for plain-text SEO fields (meta_title, meta_description) where entities are not allowed.
 */
export const ENCODING_ERROR_PATTERNS = [
  ...ENCODING_ERROR_PATTERNS_STRICT,
  /&#\d+;/,                   // HTML numeric entities
  /&[a-z]+;/i,                // HTML named entities
];

export const CONTROL_CHAR_PATTERN = /[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/;

export const BIDI_MARKER_PATTERN = /[\u200E\u200F\u202A-\u202E\u2066-\u2069]/g;

// v1.8: frontend mirror of the backend slug validator. Must match
// supabase/functions/_shared/config/quality-config.ts:VALID_SLUG_PATTERN
// exactly. Pre-v1.8 this regex was stricter than the backend's (missing
// \p{M}) — admin UI was false-rejecting Indic/Thai slugs the backend
// accepted. Catch-up + v1.8 additions in one change. Drift tracked in
// issue #352 (collapse to single source of truth).
export const VALID_SLUG_PATTERN = /^[\p{L}\p{M}\p{N}\u200C\u200D_'"()]+(?:-[\p{L}\p{M}\p{N}\u200C\u200D_'"()]+)*$/u;

// ============================================================================
// FAQ STRUCTURE VALIDATION
// ============================================================================

export interface FaqItem {
  question: string;
  answer: string;
}

/**
 * How many questions a page carries, and how long each part may be.
 *
 * BACKEND MIRROR: supabase/functions/_shared/config/quality-config.ts — keep the
 * numbers identical. A page with no questions at all stays acceptable; the band
 * applies as soon as a page has one.
 */
export const FAQ_ITEM_BAND = { min: 3, max: 6, target: 4 } as const;
export const FAQ_QUESTION_LENGTH = { min: 15, max: 100 } as const;
export const FAQ_ANSWER_LENGTH = { min: 80, max: 300 } as const;

/** Script-aware factor: CJK says the same thing in fewer characters. */
export function faqLengthMultiplier(scriptType: string = 'latin'): number {
  // The same floors the topic length windows use (FAMILY_FACTORS in
  // topicCompliance): Chinese says in 0.4 of the characters what English says,
  // and a dense script in 0.6. The older 0.5/0.7 numbers reported faithful
  // Chinese answers (34 characters against a 164-character English source) as
  // truncated, which is the "Questions and answers" problem on every CJK row.
  // 'wide' was missing, so German, Finnish, Hungarian, Russian and Ukrainian
  // were judged at the English window while every other field scaled them by
  // FAMILY_FACTORS (max 1.2). Measured 2026-09-21, the answers that broke the
  // window did so by 1 to 9 characters — 301 to 309 against 300 — and hu and uk
  // were among them. 1 / 1.2 gives those languages the same 1.2 headroom the
  // rest of their fields already had.
  return scriptType === 'cjk' ? 0.4
    : scriptType === 'indic' ? 0.6
    : scriptType === 'wide' ? 1 / 1.2
    : 1.0;
}

function normalizedQuestion(question: string): string {
  return question.trim().toLowerCase().replace(/\s+/g, ' ').replace(/[?？¿؟⁇՞;]+$/, '');
}

/**
 * Languages that do not sign a question at all: Thai, Chinese, Japanese and
 * Korean mark it with a word inside the sentence. Asking them for a question
 * mark flags correct writing as broken.
 */
const UNMARKED_QUESTION_LANGUAGES = new Set(['th', 'zh', 'ja', 'ko']);

/** The sign each language ends a question with. */
const QUESTION_MARKS_BY_LANGUAGE: Record<string, RegExp> = {
  el: /[;؟?？]$/,
  hy: /[՞?？]$/,
  ar: /[؟?？]$/,
  fa: /[؟?？]$/,
  ur: /[؟?？]$/,
  he: /[?？]$/,
};

/**
 * The per-question and per-answer windows for one writing system.
 *
 * Exported because the writer has to be told these numbers, and the only way to
 * be sure it is told the same numbers it is judged by is for both to read this.
 * On 2026-09-20 the contract stated `faq_items: 3–6 questions` and nothing about
 * the characters inside them, so 628 rows carried an answer over its ceiling —
 * the largest single cause of `malformed`, and one the writer was never warned
 * about.
 */
export function faqLengthWindows(scriptType: string = 'latin'): {
  question: { min: number; max: number };
  answer: { min: number; max: number };
} {
  const multiplier = faqLengthMultiplier(scriptType);
  return {
    question: {
      min: Math.floor(FAQ_QUESTION_LENGTH.min * multiplier),
      max: Math.ceil(FAQ_QUESTION_LENGTH.max / multiplier),
    },
    answer: {
      min: Math.floor(FAQ_ANSWER_LENGTH.min * multiplier),
      max: Math.ceil(FAQ_ANSWER_LENGTH.max / multiplier),
    },
  };
}

/** Default set, used when the language is unknown: every mark we accept. */
const ANY_QUESTION_MARK = /[?？؟⁇՞;]$/;

/**
 * Is this written as a question in its own language?
 *
 * The mark is judged by the language, not by English punctuation: Arabic and
 * Persian end with ؟, Greek with ;, Armenian with ՞, and Thai, Chinese,
 * Japanese and Korean use no sign.
 */
export function isWrittenAsQuestion(question: string, locale?: string): boolean {
  const text = question.trim();
  if (!text) return false;
  const base = (locale ?? '').split('-')[0].toLowerCase();
  if (!base) return ANY_QUESTION_MARK.test(text);
  if (UNMARKED_QUESTION_LANGUAGES.has(base)) return true;
  return (QUESTION_MARKS_BY_LANGUAGE[base] ?? /[?？]$/).test(text);
}

/**
 * Validate FAQ items structure: the count band, each item's windows, and
 * repeated questions.
 * @returns null if valid, error message if invalid
 */
export function validateFaqStructure(
  faqItems: unknown,
  scriptType?: string,
  locale?: string,
): string | null {
  if (!Array.isArray(faqItems)) {
    return 'faq_items must be an array';
  }

  if (faqItems.length === 0) {
    return null; // A page with no questions at all stays acceptable
  }

  if (faqItems.length < FAQ_ITEM_BAND.min) {
    return `faq_items has only ${faqItems.length} question(s) — at least ${FAQ_ITEM_BAND.min} are needed (aim for ${FAQ_ITEM_BAND.target})`;
  }
  if (faqItems.length > FAQ_ITEM_BAND.max) {
    return `faq_items has ${faqItems.length} questions — at most ${FAQ_ITEM_BAND.max} are allowed`;
  }

  // The caller's script family when it gave one, the language's own otherwise —
  // resolved identically in the backend mirror.
  const resolvedScript = scriptType ?? (locale ? getScriptTypeForLocale(locale) : 'latin');
  const { question: questionWindow, answer: answerWindow } = faqLengthWindows(resolvedScript);
  const minQuestionLen = questionWindow.min;
  const maxQuestionLen = questionWindow.max;
  const minAnswerLen = answerWindow.min;
  const maxAnswerLen = answerWindow.max;

  const seen = new Set<string>();

  for (let i = 0; i < faqItems.length; i++) {
    const item = faqItems[i];
    if (!item || typeof item !== 'object') {
      return `faq_items[${i}] is not an object`;
    }
    
    const typedItem = item as Record<string, unknown>;
    
    if (typeof typedItem.question !== 'string') {
      return `faq_items[${i}].question must be a string`;
    }
    if (typedItem.question.length < minQuestionLen) {
      return `faq_items[${i}].question is too short (min ${minQuestionLen} chars for ${scriptType})`;
    }
    if (typedItem.question.length > maxQuestionLen) {
      return `faq_items[${i}].question is too long (max ${maxQuestionLen} chars for ${scriptType})`;
    }
    if (!isWrittenAsQuestion(typedItem.question, locale)) {
      return `faq_items[${i}].question is not written as a question`;
    }

    const key = normalizedQuestion(typedItem.question);
    if (seen.has(key)) {
      return `faq_items[${i}].question repeats an earlier question`;
    }
    seen.add(key);
    
    if (typeof typedItem.answer !== 'string') {
      return `faq_items[${i}].answer must be a string`;
    }
    if (typedItem.answer.length < minAnswerLen) {
      return `faq_items[${i}].answer is too short (min ${minAnswerLen} chars for ${scriptType})`;
    }
    if (typedItem.answer.length > maxAnswerLen) {
      return `faq_items[${i}].answer is too long (max ${maxAnswerLen} chars for ${scriptType})`;
    }
  }
  
  return null;
}


// ============================================================================
// EMBED MARKER PATTERNS (Excluded from QA validation)
// ============================================================================

/**
 * Patterns for embed markers that should be stripped before quality validation.
 * These are functional content (video embeds, etc.) that are pure ASCII by design.
 * They should NOT affect script ratio calculations.
 * 
 * Extensible for future embed types (YouTube, Vimeo, etc.)
 */
export const EMBED_MARKER_PATTERNS = [
  // Wistia video embeds: <!-- WISTIA:abc123 --> or &lt;!-- WISTIA:abc123 --&gt;
  /<!--\s*WISTIA:\w+\s*-->/gi,
  /&lt;!--\s*WISTIA:\w+\s*--&gt;/gi,
  // Future: Add YouTube, Vimeo, etc. patterns here
] as const;

// ============================================================================
// MODEL OUTPUT CORRUPTION DETECTION
// ============================================================================

/**
 * Patterns that indicate corrupted AI model output
 * These are artifacts from tokenizer/debug leakage that should NEVER be stored
 * 
 * Examples of corrupted output:
 * - "5)    _ | _ (3)    T a x . N e t w o r k (11)   ..."
 * - Token count leaks like "(11)   ..."
 * - Spaced-out brand names like "T a x . N e t"
 */
export const MODEL_CORRUPTION_PATTERNS = [
  /\d+\)\s+_\s*\|/,           // Tokenizer fragment: "5)    _ |"
  /\(\d+\)\s*\.{3,}/,         // Token count leak: "(11)   ..."
  /^\s*_\s+\|\s+_/,           // Debug separator: "_ | _"
  /^[\s\d\)\(_\|\.]+$/,       // Only garbage chars (entire string)
  /T\s+a\s+x\s+\.\s+N\s+e/,   // Spaced-out brand: "T a x . N e t"
  /\s{3,}/,                   // Three or more consecutive spaces (tokenizer leak)
] as const;

/**
 * Detect if a value contains corrupted AI model output
 * Used to prevent garbage tokenizer/debug output from being saved to DB
 */
export function hasModelCorruption(value: string): boolean {
  if (!value || value.length < 5) return false;
  return MODEL_CORRUPTION_PATTERNS.some(pattern => pattern.test(value));
}

/**
 * Strip embed markers from content before quality validation.
 * Preserves the actual content, just removes markers for script ratio calculation.
 */
export function stripEmbedMarkersForValidation(content: string): string {
  return EMBED_MARKER_PATTERNS.reduce(
    (result, pattern) => result.replace(pattern, ''),
    content
  );
}

/**
 * Strip HTML tags from content for quality validation.
 * Used for body/content fields that contain legitimate HTML markup.
 * 
 * Processing:
 * 1. Remove all HTML tags (<p>, <h2>, class="...", etc.)
 * 2. Decode common HTML entities (&nbsp;, &amp;, etc.)
 * 3. Normalize whitespace
 */
export function stripHtmlTagsForValidation(content: string): string {
  return content
    .replace(/<[^>]*>/g, ' ')          // Remove HTML tags
    .replace(/&nbsp;/g, ' ')           // Convert &nbsp;
    .replace(/&amp;/g, '&')            // Convert &amp;
    .replace(/&lt;/g, '<')             // Convert &lt;
    .replace(/&gt;/g, '>')             // Convert &gt;
    .replace(/&quot;/g, '"')           // Convert &quot;
    .replace(/&#\d+;/g, '')            // Remove numeric entities
    .replace(/\s+/g, ' ')              // Normalize whitespace
    .trim();
}

/**
 * Combined content sanitizer for quality validation.
 * Strips both embed markers AND HTML tags.
 * Used for translated_body and similar HTML content fields.
 * 
 * Processing order:
 * 1. Strip embed markers (<!-- WISTIA:xxx -->)
 * 2. Strip HTML tags (<p>, <h2>, class="...", etc.)
 * 3. Decode HTML entities
 */
export function sanitizeForQualityValidation(content: string): string {
  const withoutMarkers = stripEmbedMarkersForValidation(content);
  return stripHtmlTagsForValidation(withoutMarkers);
}

// ============================================================================
// TRANSLATION QUALITY UTILITIES (separate module)
// ============================================================================
// For badge-level quality counting: import from '@/lib/quality/translationQualityUtils'
// - countQualityTranslations() - Returns filled/valid/issues counts
// - getRequiredFieldsForEntity() - Alias for getCompletenessFieldsForEntity()
// - Reads cached quality_* data only — frontend never classifies (issue #352)
// ============================================================================
